/* istanbul ignore file */
import Text from '@zenbusiness/common-components/Text';
import { NavLink as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';

const menuItemMixin = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  gap: '0.5rem',
  padding: 0,
  textDecoration: 'none',
  maxWidth: '100%',

  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: 'transparent'
  },

  '& .MuiTypography-root': {
    flexGrow: 1
  },

  '& span.expand': {
    flexBasis: 'auto'
  },

  '&.is-disabled': {
    pointerEvents: 'none',

    '& .MuiTypography-root, &:hover .MuiTypography-root': {
      color: theme.palette.focus30.main
    }
  },

  [theme.breakpoints.up('md')]: {
    '& .MuiTypography-root': {
      transition: theme.transitions.create(['opacity'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.short
      }),
      opacity: 0,

      '.site-navigation:focus-within &, .site-navigation:hover &': {
        opacity: 1
      }
    }
  }
});

export const StyledIcon = styled(Box)(({ theme }) => ({
  borderRadius: '0.5rem',
  boxShadow: `inset 0 0 0 0rem ${(
    theme.palette.mode === 'light'
      ? theme.palette.focus70.main
      : theme.palette.focus00.main
  )}`,
  display: 'flex',
  alignItems: 'center',
  flexBasis: '3rem',
  flexShrink: 0,
  justifyContent: 'center',
  height: '3rem',

  '& img': {
    maxWidth: '1rem',
    [theme.breakpoints.up('md')]: {
      maxWidth: '1.25rem'
    }
  },
  '& img.active': {
    opacity: 0,
    position: 'absolute'
  },
  '& img.default': {
    opacity: 1
  },
  '.menu-item.opened &, .menu-item.active &, .menu-item:hover &': {
    'img.active': {
      opacity: 1
    },
    'img.default': {
      opacity: 0
    }
  },

  '.menu-item.opened &.tile, .menu-item:hover &.tile': {
    boxShadow: `inset 0 0 0 0.0625rem ${(
      theme.palette.mode === 'light'
        ? theme.palette.focus70.main
        : theme.palette.focus00.main
    )}`
  },
  '.menu-item.active &.tile': {
    background: theme.palette.mode === 'light'
      ? theme.palette.focus70.main
      : theme.palette.focus00.main
  },
  '.menu-item.opened &.expand': {
    transform: 'rotate(180deg)'
  }
}));

export const StyledExternalLink = styled(MuiLink)(
  ({ theme }) => menuItemMixin(theme)
);

export const StyledLink = styled(RouterLink)(
  ({ theme }) => menuItemMixin(theme)
);

export const StyledButton = styled(ListItemButton)(
  ({ theme }) => menuItemMixin(theme)
);

export const StyledNav = styled(Grid)(({ theme }) => ({
  '--nav-width': '5.75rem',
  '--nav-width-wide': '17.5rem',

  background: theme.palette.focus80.main,
  color: theme.palette.focus90.contrastText,
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  height: '100vh',
  overflowX: 'hidden',
  position: 'sticky',
  top: '0',
  width: 'var(--nav-width-wide)',
  zIndex: theme.zIndex.drawer
}));

export const StyleBranding = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '5.75rem',
    lineHeight: '1',
    paddingInline: '1.5rem'
  }
}));

export const StyledMenu = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',

  '& > li': {
    alignItems: 'inherit',
    flexDirection: 'column',
    paddingInlineStart: '0.5rem',
    paddingInlineEnd: '1rem'
  },

  '& li li': {
    '& a': {
      paddingBlock: '0.75rem'
    }
  },

  [theme.breakpoints.up('md')]: {
    '& > li': {
      paddingInline: '1.375rem'
    }
  }
}));

export const StyledSubMenu = styled(List)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  margin: '0',
  padding: '0',
  paddingInlineStart: '3.5rem'
}));

export const StyledText = styled(Text)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.focus00.main,
  '.menu-item:hover &, .menu-item.opened &, .menu-item.active &': {
    color: theme.palette.mode === 'light'
      ? theme.palette.growth20.main
      : theme.palette.growth30.main
  }
}));
